// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../BackdropProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClearIcon from "@material-ui/icons/Clear";
import CustomSnackbar from '../ui/Snackbars';
import EnhancedTableHead from '../ui/EnhancedTableHead';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import SearchIcon from "@mui/icons-material/Search";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import wso2Theme from '../../theme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { adjustForTimezone, getDateForDB, getCountryList, truncateString, getComparator, stableSort } from '../../utils/formatting';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getEmployeeEmails, isAccessGranted } from '../../utils/oauth';
import { travelStatus, travelStatusArray } from '../../utils/const';
import { postSearchTravelRequests, getTravelAgents, postAgentTravelRequests } from '../../utils/end-points.js';
import { getTravelStatusColor, getTravelStatusFontColor } from '../../theme/status-colors';
import { IconButton } from "@material-ui/core";
import { APP_CONFIG, NO_VALID_VISA_AVAILABLE_ID, PRIVILEGES, VISA_NOT_APPLICABLE_ID } from '../../Config';
import { useEffect, useState, useCallback } from 'react';
import { withRouter } from 'react-router-dom'
import "../../css/AllTravelRequests.scss"
import "../../utils/end-points.js"


const headCells = [
    { id: 'job_number', label: 'Job Number', align: 'left', disablePadding: false, sortable: true },
    { id: 'traveller_email', label: "Traveller's Email", align: 'left', disablePadding: false, sortable: true },
    { id: 'country', label: 'Destination Country', align: 'left', disablePadding: false, sortable: true },
    { id: 'city', label: 'Travel Location', align: 'left', disablePadding: false, sortable: true },
    { id: 'start_date', label: 'Start Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'end_date', label: 'End Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'travel_agent_id', label: 'Travel Agent', align: 'left', disablePadding: false, sortable: false },
    { id: 'visa_id', label: 'Visa Information', align: 'left', disablePadding: false, sortable: false },
    { id: 'status', label: 'Travel Status', align: 'left', disablePadding: false, sortable: false },
];

const AllTravelRequests = ({ history, location }) => {

    const employees = getEmployeeEmails()
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState(null)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [travelRequests, setTravelRequests] = useState([])
    const [travelAgents, setTravelAgents] = useState([])
    const [emailFilter, setEmailFilter] = useState(null)
    const [countryFilter, setCountryFilter] = useState(null)
    const [travelAgentFilter, setTravelAgentFilter] = useState(null)
    const [travelStatusFilter, setTravelStatusFilter] = useState(null)
    const [startDateFilter, setStartDateFilter] = useState(null)
    const [endDateFilter, setEndDateFilter] = useState(null)
    const [searchFilter, setSearchFilter] = useState("")
    const [selectedTravel, setSelectedTravel] = useState(null)
    const [isPageLoading, setPageLoading] = useState(true)
    const [isTableLoading, setTableLoading] = useState(true)
    const [isTablePaginating, setTablePaginating] = useState(false)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const { handleRequest } = useHttp();

    const syncFiltersToQuery = () => {
        const params = new URLSearchParams();
        if (emailFilter?.workEmail) params.set("email", emailFilter.workEmail);
        if (countryFilter?.country) params.set("country", countryFilter.country);
        if (travelAgentFilter) params.set("travelAgentId", travelAgentFilter);
        if (travelStatusFilter) params.set("status", travelStatusFilter);
        if (startDateFilter) params.set("startDate", getDateForDB(startDateFilter));
        if (endDateFilter) params.set("endDate", getDateForDB(endDateFilter));
        if (searchFilter) params.set("search", searchFilter);

        history.replace({ search: params.toString() });
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const email = params.get("email");
        const country = params.get("country");
        const agentId = params.get("travelAgentId");
        let status = params.get("status");
        const start = params.get("startDate");
        const end = params.get("endDate");
        const search = params.get("search");

        if (email) setEmailFilter({ workEmail: email });
        if (country) setCountryFilter({ country });
        if (agentId) setTravelAgentFilter(+agentId);
        if (status) setTravelStatusFilter(status);
        if (start) setStartDateFilter(new Date(start));
        if (end) setEndDateFilter(new Date(end));
        if (search) setSearchFilter(search);
    }, []);

    useEffect(() => {
        fetchTravelAgents(rowsPerPage)
    }, [])

    useEffect(() => {
        syncFiltersToQuery();
        fetchTravels()
    }, [rowsPerPage, travelAgents, emailFilter, countryFilter, travelAgentFilter, travelStatusFilter, startDateFilter, endDateFilter, orderBy, order, searchFilter])

    useEffect(() => {
        if(window.innerHeight > 900) {
            setRowsPerPage(15)
        }
    }, [window.innerHeight])

    const fetchTravelAgents = useCallback(async (limit) => {
        setPageLoading(true)
        handleRequest(getTravelAgents + "?status=all", "GET", null, (travelAgents) => {
            if (travelAgents) {
                setTravelAgents(travelAgents)
            }
            else {
                setSnackbarData({ message: "Error while fetching travel agents", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setPageLoading(false)
        }, (error) => {
            setPageLoading(false)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })

    }, [])

    const fetchTravels = useCallback(async () => {
        setTableLoading(true)
        if (isAccessGranted([PRIVILEGES.ALL_TRAVEL_REQUESTS])) {
            let url = postSearchTravelRequests
            let body = {
                status: travelStatusFilter ?? "all",
                limit: rowsPerPage,
                offset: 0,
                searchString: searchFilter,
                email: emailFilter?.workEmail,
                country: countryFilter?.country,
                travelAgentId: travelAgentFilter,
                startDate: getDateForDB(startDateFilter),
                endDate: getDateForDB(endDateFilter),
                sortColumn: orderBy,
                sortOrder: order
            }
            handleRequest(url, "POST", body, (travels) => {
                if (travels) {
                    travels.map(travel => {
                        let travelAgentName = travelAgents.filter(agent => agent.id === travel.travelAgentId).length > 0
                            ? travelAgents.filter(agent => agent.id === travel.travelAgentId)[0].nameOfTravelAgent
                            : ""
                        travel.travelAgentName = travelAgentName
                    })
                    setTravelRequests(travels)
                    setSelectedTravel(travels[0])
                    setPage(0)
                }
                else {
                    setSnackbarData({ message: "Error while fetching travel requests", open: true, severity: "error", onClose: onCloseSnackbar })
                }
                setTableLoading(false)
            }, (error) => {
                setTableLoading(false)
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            })
        }

        else if (isAccessGranted([PRIVILEGES.AGENT_TRAVEL_REQUESTS])) {
            let url = postAgentTravelRequests

            let body = {
                status: travelStatusFilter ?? "all",
                limit: rowsPerPage,
                offset: 0,
                searchString: searchFilter,
                email: emailFilter?.workEmail,
                country: countryFilter?.country,
                startDate: getDateForDB(startDateFilter),
                endDate: getDateForDB(endDateFilter),
                sortColumn: orderBy,
                sortOrder: order
            }

            handleRequest(url, "POST", body, (travels) => {
                if (travels) {
                    travels.map(travel => {
                        let travelAgentName = travelAgents.filter(agent => agent.id === travel.travelAgentId).length > 0
                            ? travelAgents.filter(agent => agent.id === travel.travelAgentId)[0].nameOfTravelAgent
                            : ""
                        travel.travelAgentName = travelAgentName
                    })
                    setTravelRequests(travels)
                    setSelectedTravel(travels[0])
                    setPage(0)
                }
                else {
                    setSnackbarData({ message: "Error while fetching travel requests", open: true, severity: "error", onClose: onCloseSnackbar })
                }
                setTableLoading(false)
            }, (error) => {
                setTableLoading(false)
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            })
        }
        else {
            setTravelRequests([])
            setTableLoading(false)
            setSnackbarData({ message: "You don't have permission to get travels", open: true, severity: "error", onClose: onCloseSnackbar })
        }   

    }, [rowsPerPage, searchFilter, emailFilter, countryFilter, travelAgentFilter, travelStatusFilter, startDateFilter, endDateFilter, orderBy, order, travelAgents])

    const loadMoreTravels = (offset) => {
        setTablePaginating(true)

        if (isAccessGranted([PRIVILEGES.ALL_TRAVEL_REQUESTS])) {
            let url = postSearchTravelRequests

            let body = {
                status: travelStatusFilter ?? "all",
                limit: rowsPerPage,
                offset: offset,
                searchString: searchFilter,
                email: emailFilter?.workEmail,
                country: countryFilter?.country,
                travelAgentId: travelAgentFilter,
                startDate: getDateForDB(startDateFilter),
                endDate: getDateForDB(endDateFilter),
                sortColumn: orderBy,
                sortOrder: order
            }

            handleRequest(url, "POST", body, (travels) => {
                if (travels) {
                    travels.map(travel => {
                        let travelAgentName = travelAgents.filter(agent => agent.id === travel.travelAgentId).length > 0
                            ? travelAgents.filter(agent => agent.id === travel.travelAgentId)[0].nameOfTravelAgent
                            : ""
                        travel.travelAgentName = travelAgentName
                    })

                    let travelsList = travelRequests.concat(travels)
                    setTravelRequests(travelsList)
                    if (travels.length === 0) {
                        setPage(page => page - 1)
                        setSnackbarData({ message: "No more data", open: true, severity: "success", onClose: onCloseSnackbar })
                    }
                }
                else {
                    setSnackbarData({ message: "Error while fetching travel requests", open: true, severity: "error", onClose: onCloseSnackbar })
                }
                setTablePaginating(false)
            }, (error) => {
                setTablePaginating(false)
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            })
        }

        else if (isAccessGranted([PRIVILEGES.AGENT_TRAVEL_REQUESTS])) {
            let url = postAgentTravelRequests

            let body = {
                status: travelStatusFilter ?? "all",
                limit: rowsPerPage,
                offset: offset,
                searchString: searchFilter,
                email: emailFilter?.workEmail,
                country: countryFilter?.country,
                startDate: getDateForDB(startDateFilter),
                endDate: getDateForDB(endDateFilter),
                sortColumn: orderBy,
                sortOrder: order
            }

            handleRequest(url, "POST", body, (travels) => {
                if (travels) {
                    travels.map(travel => {
                        let travelAgentName = travelAgents.filter(agent => agent.id === travel.travelAgentId).length > 0
                            ? travelAgents.filter(agent => agent.id === travel.travelAgentId)[0].nameOfTravelAgent
                            : ""
                        travel.travelAgentName = travelAgentName
                    })
                    let travelsList = travelRequests.concat(travels)
                    setTravelRequests(travelsList)
                    if (travels.length === 0) {
                        setPage(page => page - 1)
                        setSnackbarData({ message: "No more data", open: true, severity: "success", onClose: onCloseSnackbar })
                    }
                }
                else {
                    setSnackbarData({ message: "Error while fetching travel requests", open: true, severity: "error", onClose: onCloseSnackbar })
                }
                setTablePaginating(false)
            }, (error) => {
                setTablePaginating(false)
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            })
        }
        else {
            setTravelRequests([])
            setTablePaginating(false)
            setSnackbarData({ message: "You don't have permission to get travels", open: true, severity: "error", onClose: onCloseSnackbar })
        }
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    };

    const handleClick = (travel) => {
        setSelectedTravel(travel)
        history.push(APP_CONFIG.PAGES.EDIT_TRAVEL_REQUEST + "/" + travel.jobNumber)
    };

    const handleChangePage = (event, newPage) => {
        if (travelRequests.length <= newPage * rowsPerPage) {
            loadMoreTravels(travelRequests.length)
        }
        setPage(newPage)
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    };

    const handleEmailFilter = (event, value) => {
        setEmailFilter(value)
    }

    const handleCountryFilter = (event, value) => {
        setCountryFilter(value)
    }

    const handleTravelAgentFilter = (event, value) => {
        setTravelAgentFilter(value ? value.id : null)
    }

    const handleTravelStatusFilter = (event, value) => {
        setTravelStatusFilter(value ?? null)
    }

    const handleSearchFilter = (event) => {
        setSearchFilter(event.target.value)
        if (!event.target.value) {
            fetchTravels()
        }
    }

    const getVisaInfo = (travel) => {
        if (travel.visaId) {
            if (travel.visaId === NO_VALID_VISA_AVAILABLE_ID.id) {
                return <span>No Valid Visa Available</span>
            }
            else if (travel.visaId === VISA_NOT_APPLICABLE_ID.id) {
                return <span>Visa Not Applicable</span>
            }
            else {
                return (<span>Visa Available</span>)
            }
        }
        else if (travel.visa) {
            if (travel.visa === "Visa Not Applicable" || travel.visa === "Valid Visa Available") {
                return <span>{travel.visa}</span>
            }
            else {
                return (
                    <span>Visa Available</span>
                )
            }
        }
        else {
            return ""
        }
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (

        <div className='All-travel-requests'>
            <CustomSnackbar snackbar={snackbarData} />
            <BackdropProgress open={isPageLoading} />
            <div className='Page-title'>All Travel Requests</div>
            <React.Fragment>
                {!isPageLoading && (
                    <Grid container>
                        <Grid item lg={12} className='Table-grid'>

                            <Paper className='Border-elevation'  >
                                <div className='Search-and-filters' style={{ borderColor: wso2Theme.palette.primary.light }}>
                                    <div className="Table-search-field">
                                        <TextField variant="outlined" label="Search" size="small"
                                            className='Search-text-fields'
                                            onChange={handleSearchFilter} />

                                        <Button variant={"contained"} startIcon={<SearchIcon />} color="secondary" className='Filter-button'
                                            onClick={fetchTravels} >Search</Button>
                                        <Button variant={filtersExpanded ? "contained" : "outlined"} startIcon={<FilterAltIcon />} color="primary" className='Filter-button'
                                            onClick={() => setFiltersExpanded(!filtersExpanded)} >{filtersExpanded ? "Hide Filters" : "Show Filters"}</Button>
                                    </div>
                                    {filtersExpanded && (
                                        <Grid container className='Filters-div'>
                                            <Grid item lg={2} md={4} className='Filter-grid-item-email'>
                                                <Autocomplete
                                                    size="small"
                                                    variant="standard"
                                                    noOptionsText="No options"
                                                    value={emailFilter}
                                                    disablePortal
                                                    options={employees.filter(emp => emp.workEmail !== null)}
                                                    getOptionLabel={(option) => option.workEmail}
                                                    onChange={handleEmailFilter}
                                                    renderOption={(props, option) => (
                                                        <Box {...props} key={option.workEmail}>{option.workEmail}</Box>
                                                    )}
                                                    renderInput={(params) => <TextField  {...params} label="Traveller's Email" />}
                                                />
                                            </Grid>
                                            <Grid item lg={2} md={4} className='Filter-grid-item-country'>
                                                <Autocomplete
                                                    size="small"
                                                    disablePortal
                                                    value={countryFilter ?? null}
                                                    options={getCountryList()}
                                                    getOptionLabel={(option) => option.country}
                                                    onChange={handleCountryFilter}
                                                    renderOption={(props, option) => (
                                                        <Box {...props}>{option.country}</Box>
                                                    )}
                                                    renderInput={(params) => <TextField  {...params} label="Country" />}
                                                />
                                            </Grid>
                                            <Grid item lg={2} md={4} className='Filter-grid-item-agent'>
                                                <Autocomplete
                                                    loading
                                                    disabled={!isAccessGranted([PRIVILEGES.ALL_TRAVEL_REQUESTS])}
                                                    value={travelAgents.find(agent => agent.id === travelAgentFilter) ?? null}
                                                    size="small"
                                                    disablePortal
                                                    options={travelAgents}
                                                    getOptionLabel={(option) => option.nameOfTravelAgent}
                                                    onChange={handleTravelAgentFilter}
                                                    renderOption={(props, option) => (
                                                        <Box {...props}>{option.nameOfTravelAgent}</Box>
                                                    )}
                                                    renderInput={(params) => <TextField  {...params} label="Travel Agent" />}
                                                />
                                            </Grid>
                                            <Grid item lg={2} md={4} className='Filter-grid-item-status'>
                                                <Autocomplete
                                                    size="small"
                                                    disablePortal
                                                    value={travelStatusFilter ?? null}
                                                    options={travelStatusArray}
                                                    getOptionLabel={(status) => status}
                                                    onChange={handleTravelStatusFilter}
                                                    renderOption={(props, status) => (
                                                        <Box {...props} >
                                                            <span className="Status-dot-dropdown" style={{ backgroundColor: getTravelStatusColor(status) }}></span>
                                                            {status}
                                                        </Box>
                                                    )}
                                                    renderInput={(params) => <TextField  {...params} label="Travel Status" />}
                                                />
                                            </Grid>
                                            <Grid item lg={2} md={4} className='Filter-grid-item-start-date'>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        label="Start Date"
                                                        inputFormat='yyyy-MM-dd'
                                                        mask='____-__-__'
                                                        value={startDateFilter}
                                                        onChange={(newValue) => {
                                                            setStartDateFilter(newValue);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <IconButton onClick={() => setStartDateFilter(null)} size="small">
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            )
                                                        }}
                                                        InputAdornmentProps={{
                                                            position: "start"
                                                        }}
                                                        renderInput={(params) => <TextField size="small" {...params} fullWidth />}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid item lg={2} md={4} className='Filter-grid-item-end-date'>
                                                <LocalizationProvider dateAdapter={AdapterDateFns} >
                                                    <DatePicker
                                                        label="End Date"
                                                        inputFormat='yyyy-MM-dd'
                                                        mask='____-__-__'
                                                        value={endDateFilter}
                                                        onChange={(newValue) => {
                                                            setEndDateFilter(newValue);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <IconButton onClick={() => setEndDateFilter(null)} size="small">
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            )
                                                        }}
                                                        InputAdornmentProps={{
                                                            position: "start"
                                                        }}
                                                        renderInput={(params) => <TextField size="small" {...params} fullWidth />}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                        </Grid>
                                    )}
                                </div>
                                {isTableLoading && <LinearProgress color='secondary' />}
                                <TableContainer className='Table-container'>
                                    <Table aria-labelledby="tableTitle" size={'small'} >
                                        <EnhancedTableHead headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                        <TableBody>
                                            {selectedTravel && stableSort(travelRequests, getComparator(order, orderBy))
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row) => {
                                                    return (
                                                        <TableRow onClick={(event) => handleClick(row)} tabIndex={-1} key={row.jobNumber}
                                                            className='Selected-row'
                                                            style={{
                                                                backgroundColor: selectedTravel.jobNumber === row.jobNumber ? "white" : null,
                                                                elevation: selectedTravel.jobNumber === row.jobNumber ? "above" : null,
                                                            }}>
                                                            <TableCell className='Table-cell Table-cell-job-number'>{truncateString(row.jobNumber, 20)}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-email'>{truncateString(row.travellerEmail, 20)}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-country'>{truncateString(row.country, 20)}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-location'>{truncateString(row.city, 17)}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-date' style={{ backgroundColor: getDateForDB(adjustForTimezone(new Date(row.startDate))) >= getDateForDB(adjustForTimezone(new Date())) ? getTravelStatusColor(travelStatus.COMPLETED) : wso2Theme.palette.colors.delete }}>
                                                                {getDateForDB(adjustForTimezone(new Date(row.startDate)))}
                                                            </TableCell>
                                                            <TableCell className='Table-cell Table-cell-date'>{getDateForDB(adjustForTimezone(new Date(row.endDate)))}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-agent'>{truncateString(row.travelAgentName, 20)}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-visa'>{getVisaInfo(row)}</TableCell>
                                                            <TableCell className='Table-cell Table-cell-status' >
                                                                <span className='Status-chip'
                                                                    style={{
                                                                        backgroundColor: getTravelStatusColor(row.status),
                                                                        color: getTravelStatusFontColor(row.status)
                                                                    }}>
                                                                    {row.status}
                                                                </span>
                                                            </TableCell>
                                                        </TableRow>

                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {isTablePaginating && <LinearProgress color='secondary' />}

                                {!isTableLoading && !isTablePaginating && travelRequests.length === 0 && <div className='No-requests-label'>No Travel Requests</div>}
                                {(isTableLoading || isTablePaginating) && <div className='No-requests-label'>Please wait ...</div>}

                                <TablePagination
                                    className='Table-pagination'
                                    rowsPerPageOptions={[10, 15, 25, 50]}
                                    component="div"
                                    count={-1}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </React.Fragment>
        </div>
    );
}

export default withRouter(AllTravelRequests);
